import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState } from "react";
import { BrowserRouter } from "react-router-dom";

import { BrandingProvider } from "@/app/branding/branding.context";
import { tenantSourceFase1 } from "@/app/branding/tenant-source";
import { ThemeProvider } from "@/app/theme/theme.context";
import { AuthProvider } from "@/features/auth/auth.context";

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Composición de todos los providers globales:
 *  - BrandingProvider (white-label por institución — ADR 039).
 *  - QueryClient (TanStack Query) con defaults razonables.
 *  - AuthProvider (sesión + refresh on 401).
 *  - BrowserRouter (react-router).
 *
 * Orden importa:
 *  - BrandingProvider es el más externo: el login ya es branded, así que el
 *    branding debe resolver antes que la sesión (ADR 039 §3).
 *  - ThemeProvider (modo claro/oscuro, ADR 048) envuelve a todo lo visible para
 *    que login y app respeten el tema; es ortogonal al branding (uno pinta el
 *    color de marca, el otro conmuta claro/oscuro).
 *  - AuthProvider depende del QueryClient (queries de auth); el router envuelve
 *    todo para que las rutas tengan acceso a auth y queries.
 */
export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 min
            gcTime: 5 * 60 * 1000, // 5 min
            retry: (failureCount, error) => {
              // No reintentar 4xx (validación, permisos, etc.).
              const status =
                error instanceof Error && "httpStatus" in error
                  ? Number((error as { httpStatus: number }).httpStatus)
                  : 0;
              if (status >= 400 && status < 500) return false;
              return failureCount < 2;
            },
            refetchOnWindowFocus: false,
          },
          mutations: {
            retry: false,
          },
        },
      }),
  );

  return (
    <BrandingProvider source={tenantSourceFase1}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>{children}</AuthProvider>
          </BrowserRouter>
          {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
        </QueryClientProvider>
      </ThemeProvider>
    </BrandingProvider>
  );
}
